import React, { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { fetchMorningSummary } from '../../api/dailyApi';
import { FONT_SIZE, FONT_WEIGHT, LINE_HEIGHT, RADIUS, SPACING, TOUCH_TARGET, type ThemeTokens } from '../../constants/constants';
import { useCardPage } from '../../contexts/CardPageContext';
import { useStorage } from '../../contexts/StorageContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import type { ChainStep, MorningSummary } from '../../types/daily';
import { CHAIN_STEP_LOG_JOB, writeSyncLogFilter } from '../../utils/syncLogLink';
import { AppButton } from '../common/AppButton';

/**
 * '오늘 아침 자동 작업' 카드(감사 card-controltower#566, 백엔드 PR #78 Q6 1단계) — 07:30 서버 체인(동기화 → 가격 받기 → 시세 분류 →
 * 상승분 자동 반영)의 단계별 시각 · 성공 · 실패 · 건너뜀 · 경고 · 실패 사유 상위. 예전엔 아침 작업이 돌았는지 반영 로그를 뒤져야 알았다.
 * 옛 서버(404) · 모양이 다르면 그리지 않는다. 체인이 끝나면(① '다시 받기' 포함) 다시 받는다.
 */

const STEP_STATUS_LABEL: Record<string, string> = {
  SUCCESS: '완료', FAILED: '실패', SKIPPED: '건너뜀', RUNNING: '진행 중', INTERRUPTED: '중단',
};

const statusColor = (t: ThemeTokens, status: string, warn: boolean): string => {
  if (status === 'FAILED' || warn) return t.danger;
  if (status === 'SUCCESS') return t.success;
  if (status === 'RUNNING') return t.accent;
  if (status === 'INTERRUPTED') return t.warning;
  return t.textSecondary;
};

const hm = (iso: string | null) => (iso ? dayjs(iso).format('HH:mm') : null);

/** 단계 한 줄의 수 — 시세 분류의 실패 칸은 '확인 필요' 수(실패 아님). */
function stepCounts(s: ChainStep): string {
  const parts: string[] = [];
  if (s.total > 0) parts.push(`${s.total.toLocaleString()}건`);
  if (s.step === 'CLASSIFY_DROPS') {
    if (s.failCount > 0) parts.push(`확인 필요 ${s.failCount.toLocaleString()}`);
  } else {
    if (s.successCount > 0) parts.push(`성공 ${s.successCount.toLocaleString()}`);
    if (s.failCount > 0) parts.push(`실패 ${s.failCount.toLocaleString()}`);
  }
  if (s.skippedCount > 0) parts.push(`건너뜀 ${s.skippedCount.toLocaleString()}`);
  return parts.join(' · ');
}

/** 사유 줄 — 보조색 한 줄(말줄임). 길면 '더 보기' 버튼(44px, aria-expanded)으로 전체를 펼친다(키보드 · 터치로도 읽게, 코덱 #859). */
const NOTE_LONG = 24;
const StepNote: React.FC<{ step: string; note: string; color: string; linkColor: string }> = ({ step, note, color, linkColor }) => {
  const [open, setOpen] = useState(false);
  const long = note.length > NOTE_LONG;
  return (
    <div data-testid={`morning-step-note-${step}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
      <div
        title={note}
        style={{
          fontSize: FONT_SIZE.sm, color, maxWidth: '100%',
          ...(open ? { whiteSpace: 'normal', overflowWrap: 'anywhere' } : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
        }}
      >
        {note}
      </div>
      {long && (
        <button
          type="button" aria-expanded={open} data-testid={`morning-step-note-toggle-${step}`}
          onClick={() => setOpen((v) => !v)}
          style={{
            minHeight: TOUCH_TARGET, minWidth: TOUCH_TARGET, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer',
            fontSize: FONT_SIZE.sm, color: linkColor, textDecoration: 'underline', textAlign: 'left',
          }}
        >
          {open ? '접기' : '더 보기'}
        </button>
      )}
    </div>
  );
};

export const MorningSummaryCard: React.FC = () => {
  const t = useThemeTokens();
  const { jobs } = useCardPage();
  const nav = useNavigation();
  const { store, superStore, isLoggedIn } = useStorage();
  const scope = `${isLoggedIn ? 'in' : 'out'}:${superStore ?? store ?? 'none'}`;
  const [summary, setSummary] = useState<MorningSummary | null>(null);
  // 404(옛 서버)가 아닌 오류 — 카드를 숨기지 않고 인라인 오류 + 다시 시도(감사 #662).
  const [loadError, setLoadError] = useState(false);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  // 마지막으로 받은 날짜 — 탭을 켜 둔 채 날이 바뀌면(다시 보일 때) 다시 받는다(감사 #662).
  const loadedDayRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    const at = scopeRef.current;
    loadedDayRef.current = dayjs().format('YYYY-MM-DD');
    try {
      const s = await fetchMorningSummary();
      if (at !== scopeRef.current) return;
      setSummary(s);
      setLoadError(false);
    } catch (e) {
      if (at !== scopeRef.current) return;
      const status = (e as { response?: { status?: number } })?.response?.status;
      setSummary(null);
      setLoadError(status !== 404);
    }
  }, []);

  useEffect(() => {
    setSummary(null);
    setLoadError(false);
    if (scope.startsWith('in:') && !scope.endsWith(':none')) void load();
  }, [scope, load]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!scopeRef.current.startsWith('in:') || scopeRef.current.endsWith(':none')) return;
      if (loadedDayRef.current !== dayjs().format('YYYY-MM-DD')) void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  // 서버 체인이 끝나면(running → 없음) 다시 받는다.
  const running = !!jobs.serverDaily?.running;
  const wasRunning = useRef(running);
  useEffect(() => {
    if (wasRunning.current && !running) void load();
    wasRunning.current = running;
  }, [running, load]);

  if (loadError) {
    return (
      <div data-testid="morning-summary-error" role="alert"
        style={{
          border: `1px solid ${t.lineDanger}`, borderRadius: RADIUS.lg, background: t.bgSurface,
          padding: `${SPACING.xs}px ${SPACING.md}px`,
          fontSize: FONT_SIZE.sm, color: t.danger, display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap',
        }}>
        오늘 아침 자동 작업 기록을 불러오지 못했습니다.
        <AppButton size="sm" variant="default" onClick={() => { void load(); }}>다시 시도</AppButton>
      </div>
    );
  }
  if (!summary) return null;
  const box: React.CSSProperties = {
    border: `1px solid ${t.border}`, borderRadius: RADIUS.lg, background: t.bgSurface,
    // 블록 사이 간격은 .ct-stack(16) · 안쪽은 칸 슬롯 리듬(12/16, CT 배치 규칙 §2-6).
    padding: `${SPACING.sm}px ${SPACING.md}px`,
  };

  if (!summary.ran || !summary.chain) {
    return (
      <div data-testid="morning-summary" style={{ ...box, fontSize: FONT_SIZE.sm, color: t.textSecondary }}>
        오늘 아침 자동 작업(07:30) 기록이 아직 없습니다 — ① 가격 받기로 지금 받을 수 있습니다.
      </div>
    );
  }

  const c = summary.chain;
  const anyWarn = c.status === 'FAILED' || c.steps.some((s) => s.warn || s.status === 'FAILED');
  // "실패 N건 보기" — 반영 로그를 그날 · 그 단계 · 실패만 걸러 연다(유저 플로우 제안 1). 다시 받기는 반영 로그에서(관리자).
  const summaryDate = summary.date ?? dayjs().format('YYYY-MM-DD');
  const openFailures = (s: ChainStep) => {
    const jobType = CHAIN_STEP_LOG_JOB[s.step];
    if (!jobType) return;
    // step — 반영 로그의 '실패 N건 다시 돌리기'(백엔드 PR #95)가 어느 단계를 다시 돌릴지(상승분 자동 반영 실패는 ③ 과 같은 SYNC_NAVER).
    writeSyncLogFilter({ date: summaryDate, jobType, label: `오늘 아침 자동 작업 · ${s.label}`, step: s.step });
    nav.go('syncLog');
  };
  return (
    <section
      data-testid="morning-summary"
      aria-label="오늘 아침 자동 작업"
      style={{ ...box, borderColor: anyWarn ? t.lineDanger : t.border }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACING.xs, flexWrap: 'wrap', marginBottom: SPACING.xxs }}>
        <span style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>오늘 아침 자동 작업</span>
        <span style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
          {[hm(c.startedAt), c.finishedAt ? `~ ${hm(c.finishedAt)}` : null].filter(Boolean).join(' ')}
          {' · '}
          <b style={{ color: statusColor(t, c.status, false) }}>{STEP_STATUS_LABEL[c.status] ?? c.status}</b>
        </span>
      </div>
      {/* 템플릿 A 단계 묶음(src/layout.css .ct-steps · .ct-cell — CT 배치 규칙 L2, 사장 결정 2026-11-04 D2): 단계 수만큼 같은 폭 한 줄,
          칸마다 같은 네 줄 슬롯(머리 · 수 · 사유 · 행동)이라 줄이 맞고 '실패 N건 보기' 는 칸 바닥. 본문 560 미만이면 세로(@container). */}
      <div className="ct-cq">
        <ol data-testid="morning-steps" className="ct-steps" data-n={Math.min(c.steps.length, 5)}
          style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {c.steps.map((s) => {
            const color = statusColor(t, s.status, s.warn);
            const counts = stepCounts(s);
            const reasons = s.topReasons.slice(0, 3).map((r) => `${r.reason} ${r.count.toLocaleString()}`).join(' · ');
            const note = [s.message, reasons].filter(Boolean).join(' · ');
            const warnNote = s.warn || s.status === 'FAILED';
            const canOpen = s.failCount > 0 && !!CHAIN_STEP_LOG_JOB[s.step];
            return (
              <li key={s.step} data-testid={`morning-step-${s.step}`} className="ct-cell"
                style={{ padding: 0, lineHeight: LINE_HEIGHT.relaxed }}>
                {/* ① 머리 — 단계 이름 · 상태 · 시각 */}
                <div className="ct-cell__head" style={{ display: 'flex', alignItems: 'baseline', gap: SPACING.xs, fontSize: FONT_SIZE.md, minWidth: 0 }}>
                  <span style={{ fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>{s.label}</span>{' '}
                  <span style={{ color, fontWeight: FONT_WEIGHT.semibold }}>{STEP_STATUS_LABEL[s.status] ?? s.status}</span>{' '}
                  {hm(s.finishedAt ?? s.startedAt) && <span style={{ color: t.textMuted, fontVariantNumeric: 'tabular-nums' }}>{hm(s.finishedAt ?? s.startedAt)}</span>}
                </div>
                {/* ② 수 — 건수 · 성공 · 실패 · 건너뜀(없으면 빈 자리) */}
                <div className="ct-cell__count" style={{ fontSize: FONT_SIZE.sm, color: s.warn ? t.danger : t.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                  {counts}
                </div>
                {/* ③ 사유 — 보조색, 길면 '더 보기' 로 펼침 */}
                <div className="ct-cell__reason">
                  {note && <StepNote step={s.step} note={note} color={warnNote ? t.danger : t.textSecondary} linkColor={t.accent} />}
                </div>
                {/* ④ 행동 — '실패 N건 보기'(반영 로그를 그날 · 그 단계 · 실패만으로), 칸 바닥 */}
                <div className="ct-cell__action">
                  {canOpen && (
                    <div data-testid={`morning-step-failures-row-${s.step}`} style={{ display: 'flex', alignItems: 'center' }}>
                      <AppButton variant="link" size="sm" data-testid={`morning-step-failures-${s.step}`}
                        style={{ height: TOUCH_TARGET, padding: 0, fontSize: FONT_SIZE.sm, color: t.danger }}
                        onClick={() => openFailures(s)}>
                        {`실패 ${s.failCount.toLocaleString()}건 보기`}
                      </AppButton>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};
